import { type PropsWithChildren, useMemo } from "react";
import {
  CartContext,
  type CartContextValue,
  type AddToCartInput
} from "./cart-context";
import { useAddToSellerCart } from "@/queries/cart";

/**
 * Provides cart context for the multi-seller cart architecture.
 *
 * In this architecture:
 * - No localStorage tracking needed - API manages carts per seller
 * - Adding items automatically routes to the correct seller's cart
 * - Use `useSellerCarts()` query to fetch all active carts
 */
export const CartProvider = ({ children }: PropsWithChildren) => {
  const addToCartMutation = useAddToSellerCart();

  const value: CartContextValue = useMemo(
    () => ({
      addToCart: (input: AddToCartInput) =>
        addToCartMutation.mutateAsync(input),
      isAddingToCart: addToCartMutation.isPending
    }),
    [addToCartMutation.mutateAsync, addToCartMutation.isPending]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
